import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/common';
import { queryKeys } from '@/config';
import { apiErrorMessage } from '@/lib';
import { useNotificationsStore } from '@/stores';
import { notificationsApi, notificationTemplatesApi } from './api';

/**
 * Matches the backend's own clamp (NOTIFICATIONS_PAGE_SIZE): asking for more
 * is silently trimmed there, so the two have to agree on where page 2 starts.
 */
export const NOTIFICATIONS_PAGE_SIZE = 50;

/**
 * One page of the user's notifications. Everything past the 50th used to be
 * unreachable: the list asked for a single page and never sent an offset.
 */
export function useNotifications(page = 0, options: { enabled?: boolean } = {}) {
  return useQuery({
    enabled: options.enabled ?? true,
    queryKey: queryKeys.notifications.list(page),
    queryFn: () =>
      notificationsApi.list({
        limit: NOTIFICATIONS_PAGE_SIZE,
        offset: page * NOTIFICATIONS_PAGE_SIZE,
      }),
    // Paging without this blanks the list on every step, which reads as a bug.
    placeholderData: keepPreviousData,
  });
}

export function useUnreadCount() {
  const setUnreadCount = useNotificationsStore((s) => s.setUnreadCount);

  return useQuery({
    queryKey: queryKeys.notifications.unreadCount,
    queryFn: async () => {
      const result = await notificationsApi.unreadCount();
      setUnreadCount(result.count);
      return result;
    },
    refetchInterval: 60_000,
  });
}

export function useMarkAsRead() {
  const queryClient = useQueryClient();
  const decrement = useNotificationsStore((s) => s.decrement);
  const { toast } = useToast();
  const { t } = useTranslation('notifications');

  return useMutation({
    mutationFn: (id: string) => notificationsApi.markAsRead(id),
    onSuccess: () => {
      decrement();
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
    onError: (error) => {
      toast({ title: apiErrorMessage(error, t('actionError')), variant: 'error' });
    },
  });
}

export function useMarkAllAsRead() {
  const queryClient = useQueryClient();
  const reset = useNotificationsStore((s) => s.reset);
  const { toast } = useToast();
  const { t } = useTranslation('notifications');

  return useMutation({
    mutationFn: () => notificationsApi.markAllAsRead(),
    onSuccess: () => {
      reset();
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount });
    },
    onError: (error) => {
      toast({ title: apiErrorMessage(error, t('actionError')), variant: 'error' });
    },
  });
}

/**
 * Deletes one notification (#54). The backend only hides the row, so the
 * unread badge is refreshed from the server rather than guessed here.
 *
 * A 404 counts as done: the backend answers 404 for a row that is already
 * deleted (another device or tab got there first), and the user's goal - the
 * notification is gone - is met. Treating it as an error left the ghost row
 * on screen and failed the same way on every retry. The list is refetched on
 * every outcome, so even a real error does not leave a stale row behind.
 */
export function useDeleteNotification() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('notifications');

  return useMutation({
    mutationFn: async (id: string) => {
      try {
        await notificationsApi.remove(id);
      } catch (error) {
        if (isNotFound(error)) return;
        throw error;
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
    onError: (error) => {
      toast({ title: apiErrorMessage(error, t('deleteError')), variant: 'error' });
    },
  });
}

function isNotFound(error: unknown): boolean {
  return (error as { response?: { status?: number } })?.response?.status === 404;
}

export function useDeleteAllNotifications() {
  const queryClient = useQueryClient();
  const reset = useNotificationsStore((s) => s.reset);
  const { toast } = useToast();
  const { t } = useTranslation('notifications');

  return useMutation({
    mutationFn: () => notificationsApi.removeAll(),
    onSuccess: () => {
      reset();
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
    onError: (error) => {
      toast({ title: apiErrorMessage(error, t('deleteError')), variant: 'error' });
    },
  });
}

export function useNotificationRules() {
  return useQuery({
    queryKey: queryKeys.notifications.rules,
    queryFn: () => notificationsApi.getRules(),
  });
}

export function useUpdateNotificationRule() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('notifications');

  return useMutation({
    mutationFn: ({
      type,
      payload,
    }: {
      type: string;
      payload: { channels: string[]; isActive: boolean };
    }) => notificationsApi.updateRule(type, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.rules });
    },
    onError: (error) => {
      toast({ title: apiErrorMessage(error, t('actionError')), variant: 'error' });
    },
  });
}

export function useNotificationTemplates() {
  return useQuery({
    queryKey: queryKeys.notifications.templates,
    queryFn: () => notificationTemplatesApi.list(),
  });
}

export function useUpsertNotificationTemplate() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('notifications');

  return useMutation({
    mutationFn: ({
      type,
      payload,
    }: {
      type: string;
      payload: { subject: string; bodyTemplate: string };
    }) => notificationTemplatesApi.upsert(type, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.templates });
    },
    onError: (error) => {
      toast({ title: apiErrorMessage(error, t('actionError')), variant: 'error' });
    },
  });
}

export function useDeleteNotificationTemplate() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('notifications');

  return useMutation({
    mutationFn: (type: string) => notificationTemplatesApi.remove(type),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.templates });
    },
    onError: (error) => {
      toast({ title: apiErrorMessage(error, t('actionError')), variant: 'error' });
    },
  });
}
